import React, { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';

import GameModal from '../GameModal/GameModal';
import {
  playAudio as playAudioAction,
  toggleAudio,
  toggleSequence,
  stopAudio,
} from 'actions/audioActions';
import { showHelp } from 'actions/flinkPlayActions';
import { formatHtmlForPlay, isMobileAppRuntime } from 'utils';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import API from 'api';
import classes from './Help.module.scss';
import mobileHelpClasses from './mobile/HelpMobile.module.scss';
import { imagesURL } from 'config';

const WORDS_TOKEN = /\{\{\s*wordsInWordlist\s*\}\}/i;
const EMPTY_AUDIO_SEQUENCE = [];

const htmlToText = (html = '') => {
  const node = document.createElement('div');
  node.innerHTML = html;

  return String(node.textContent || node.innerText || '')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const Help = ({
  showHelp,
  help,
  translate,
  audioSrc,
  isPlaying,
  audioDebugMode,
}) => {
  const {
    title,
    stringNumber,
    cb,
    customTranslate,
    audioHelp,
    audioStartedOnOpen,
    helpAudioFilename,
    wordAudioSequence = EMPTY_AUDIO_SEQUENCE,
    useCompositeWordHelp,
    localeCode,
    activityGroupId,
    audioDebugContext,
    data,
    menuHelp,
  } = help;

  const [segmentState, setSegmentState] = useState({
    status: 'idle',
    beforeAudio: '',
    afterAudio: '',
  });
  const mobileRuntime = useMemo(() => isMobileAppRuntime(), []);
  const androidMenuHelp = mobileRuntime && menuHelp && getMobileAppPlatform() === 'android';
  const skipInitialMobileAutoplay = useRef(Boolean(mobileRuntime && audioStartedOnOpen));
  const runAudioOperation = useCallback((operation, operationName, ...args) => {
    // Preserve the original desktop execution path exactly. The guarded
    // diagnostics are enabled only inside the native mobile runtime.
    if (!mobileRuntime) {
      operation(...args);
      return true;
    }

    try {
      operation(...args);
      return true;
    } catch (error) {
      const diagnostic = {
        area: 'Help',
        operation: operationName,
        message: error?.message || String(error),
        stack: error?.stack || '',
        audioHelp: audioHelp || '',
        timestamp: new Date().toISOString(),
      };

      console.error('[FLC mobile][Help audio]', diagnostic);

      try {
        window.localStorage.setItem(
          'flc.mobile.lastHelpError',
          JSON.stringify(diagnostic)
        );
      } catch (storageError) {
        console.error('[FLC mobile][Help audio] Unable to persist diagnostic', storageError);
      }

      return false;
    }
  }, [audioHelp, mobileRuntime]);

  const playAudio = useCallback(
    (src) => runAudioOperation(playAudioAction, 'playAudio', src),
    [runAudioOperation]
  );
  const toggleSingleAudio = useCallback(
    (src) => runAudioOperation(toggleAudio, 'toggleAudio', src),
    [runAudioOperation]
  );
  const playSequence = useCallback(
    (sequence) => runAudioOperation(toggleSequence, 'toggleSequence', sequence),
    [runAudioOperation]
  );
  const stopCurrentAudio = useCallback(
    () => runAudioOperation(stopAudio, 'stopAudio'),
    [runAudioOperation]
  );

  const translateFn = customTranslate || translate;
  const html = mobileRuntime
    ? (typeof translateFn === 'function'
      ? translateFn(stringNumber, '', false, true)
      : '')
    : translateFn(stringNumber, '', false, true);
  const modalTitle = mobileRuntime
    ? (title || (typeof translateFn === 'function'
      ? translateFn(66, 'Help')
      : 'Help'))
    : (title || translateFn(66, 'Help'));

  const formattedHelp = useMemo(() => formatHtmlForPlay({ html, data }), [
    html,
    data,
  ]);

  const textParts = useMemo(() => {
    if (!html || !WORDS_TOKEN.test(html)) return null;

    const parts = html.split(WORDS_TOKEN);
    return {
      beforeText: htmlToText(parts[0] || ''),
      afterText: htmlToText(parts.slice(1).join('') || ''),
    };
  }, [html]);

  const shouldUseCompositeAudio = !!(
    useCompositeWordHelp
    && localeCode !== 'en'
    && activityGroupId
    && textParts
    && wordAudioSequence.length
  );

  useEffect(() => {
    let active = true;

    if (!shouldUseCompositeAudio) {
      setSegmentState({ status: 'idle', beforeAudio: '', afterAudio: '' });
      return undefined;
    }

    setSegmentState({ status: 'loading', beforeAudio: '', afterAudio: '' });

    const request = API.polly.generateWordAgHelpSegments({
      groupId: activityGroupId,
      locale: localeCode,
      helpAudio: helpAudioFilename,
      beforeText: textParts.beforeText,
      afterText: textParts.afterText,
    });

    const handleResult = (result) => {
      if (!active) return;

      if (result?.success) {
        setSegmentState({
          status: 'ready',
          beforeAudio: result.beforeAudio || '',
          afterAudio: result.afterAudio || '',
        });
        return;
      }

      console.error('Word AG Help audio segment generation failed', result?.error || result);
      setSegmentState({ status: 'error', beforeAudio: '', afterAudio: '' });
    };

    if (mobileRuntime) {
      request.then(handleResult).catch((error) => {
        if (!active) return;

        console.error('Word AG Help audio segment request failed', error);
        setSegmentState({ status: 'error', beforeAudio: '', afterAudio: '' });

        try {
          window.localStorage.setItem(
            'flc.mobile.lastHelpError',
            JSON.stringify({
              area: 'Help',
              operation: 'generateWordAgHelpSegments',
              message: error?.message || String(error),
              stack: error?.stack || '',
              timestamp: new Date().toISOString(),
            })
          );
        } catch (storageError) {
          console.error('[FLC mobile][Help] Unable to persist request diagnostic', storageError);
        }
      });
    } else {
      // Keep the original desktop promise behavior unchanged.
      request.then(handleResult);
    }

    return () => {
      active = false;
    };
  }, [
    activityGroupId,
    helpAudioFilename,
    localeCode,
    shouldUseCompositeAudio,
    textParts,
    mobileRuntime,
  ]);

  const compositeAudioSequence = useMemo(() => {
    if (segmentState.status !== 'ready') return [];

    return [
      segmentState.beforeAudio,
      ...wordAudioSequence,
      segmentState.afterAudio,
    ].filter(Boolean);
  }, [
    segmentState.afterAudio,
    segmentState.beforeAudio,
    segmentState.status,
    wordAudioSequence,
  ]);

  const sequenceIsPlaying =
    compositeAudioSequence.includes(audioSrc) && isPlaying;

  const startHelpAudio = useCallback(() => {
    if (shouldUseCompositeAudio) {
      if (compositeAudioSequence.length) {
        playSequence(compositeAudioSequence);
      } else if (segmentState.status === 'error' && audioHelp) {
        playAudio(audioHelp);
      }
      return;
    }

    if (audioHelp) {
      playAudio(audioHelp);
    }
  }, [
    audioHelp,
    compositeAudioSequence,
    segmentState.status,
    shouldUseCompositeAudio,
    playAudio,
    playSequence,
  ]);

  const toggleHelpAudio = useCallback(() => {
    if (shouldUseCompositeAudio) {
      if (sequenceIsPlaying) {
        stopCurrentAudio();
      } else {
        startHelpAudio();
      }
      return;
    }

    if (audioHelp) {
      toggleSingleAudio(audioHelp);
    }
  }, [
    audioHelp,
    sequenceIsPlaying,
    shouldUseCompositeAudio,
    startHelpAudio,
    stopCurrentAudio,
    toggleSingleAudio,
  ]);

  useEffect(() => {
    if (shouldUseCompositeAudio && segmentState.status === 'loading') {
      return undefined;
    }

    // Login Help on native mobile starts audio synchronously from the Help button
    // click so iOS keeps the user gesture. Do not immediately stop/restart that
    // same file when the modal mounts. Desktop and all other Help dialogs keep
    // their existing autoplay path.
    if (skipInitialMobileAutoplay.current) {
      skipInitialMobileAutoplay.current = false;
      return () => stopCurrentAudio();
    }

    startHelpAudio();
    return () => stopCurrentAudio();
  }, [
    segmentState.status,
    shouldUseCompositeAudio,
    startHelpAudio,
    stopCurrentAudio,
  ]);

  const audioIcon = `${imagesURL}/Images/Audio/${
    (audioSrc === audioHelp && isPlaying) || sequenceIsPlaying
      ? 'audiostop'
      : 'audio_icon1'
  }.png`;

  return (
    <GameModal
      title={modalTitle}
      {...(mobileRuntime
        ? {
          outerClassnames: androidMenuHelp ? [classes.mobileMenuHelpAndroid] : [],
          innerClassnames: [
            classes.mobileModalInner,
            classes.mobileModalTypography,
          ],
          contentClassnames: [
            classes.mobileModalContent,
            classes.mobileModalTypographyContent,
            mobileHelpClasses.scrollableContent,
          ],
        }
        : {})}
      buttons={[
        {
          clickHandler: toggleHelpAudio,
          dontShow: !audioHelp && !shouldUseCompositeAudio,
          icon: audioIcon,
        },
        {
          icon: `${imagesURL}/Images/Help/Close50.png`,
          clickHandler: () => {
            stopCurrentAudio();
            cb && cb();
            showHelp(null);
          },
        },
      ]}
    >
      <div
        className={classes.wrapper}
        dangerouslySetInnerHTML={{ __html: formattedHelp }}
      />
      {mobileRuntime && audioDebugMode && (
        <div className={classes.mobileAudioDebug}>
          Context: {audioDebugContext || `${activityGroupId ? 'Menu / Activity Group Help' : 'Help dialog'} | String #${stringNumber || 'not set'} | Locale ${localeCode || 'current'}`}
          <br />
          Expected audio URL: {audioHelp || 'not configured — no stringAudio/helpAudio was resolved'}
          {shouldUseCompositeAudio && wordAudioSequence.length > 0 && (
            <>
              <br />
              Word audio URLs: {wordAudioSequence.join(' | ')}
            </>
          )}
        </div>
      )}
    </GameModal>
  );
};

Help.propTypes = {
  audioSrc: PropTypes.string,
  isPlaying: PropTypes.bool,
  customTranslate: PropTypes.func,
  translate: PropTypes.func,
  showHelp: PropTypes.func.isRequired,
  audioHelp: PropTypes.string,
  help: PropTypes.object.isRequired,
  audioDebugMode: PropTypes.bool,
};

const mapStateToProps = ({ flinkPlay, audio, common }) => ({
  audioSrc: audio.audioSrc,
  isPlaying: audio.isPlaying,
  help: flinkPlay.help,
  translate: flinkPlay.solutionTranslate,
  audioDebugMode:
    common.config?.customContentSettings?.mobileWrapper?.audioDebugMode === true,
});

export default compose(connect(mapStateToProps, { showHelp }))(Help);
